<template>
  <Dialog
    :visible="active"
    pt:root:data-sb-regression="sb-modal"
    :class="[
      'sb-ds-modal',
      showFullscreen && 'sb-ds-modal-fullscreen',
      variation === 'actionSheet' && 'sb-ds-modal-action-sheet',
    ]"
    :append-to="appendToValue"
    content-class="sb-ds-modal-content"
    :modal="!showFullscreen"
    :base-z-index="500"
    :draggable="isDraggable"
    :close-on-escape="escapeTrigger"
    :dismissable-mask="clickOutsideTrigger"
    pt:header:class="sb-ds-modal-header"
    :pt:mask:class="[
      'sb-ds-modal-mask',
      (showFullscreen || variation === 'actionSheet') && 'sb-ds-modal-mask-no-padding',
      shouldAppendTo && 'sb-ds-modal-mask-absolute'
    ]"
    :pt:mask:id="maskId"
    :close-button-props="{class: 'sb-ds-modal-close-button'}"
    :position="position"
    @update:visible="$emit('update:active', $event)"
    @show="clickOutsideTrigger && registerMaskHandler()"
    @hide="clickOutsideTrigger && unregisterMaskHandler()"
  >
    <template #closeicon>
      <SbCloseButton
        v-if="showCloseButton"
        :escape-trigger="escapeTrigger"
        data-test-id="close-button"
        @onclose="$emit('close', $event)"
      />
      <span v-else />
    </template>
    <SbLoadingPromiseWrapper
      size="sm"
      :loading-promise="loadingPromise"
      class="sb-ds-modal-loading-wrapper"
    >
      <slot />
    </SbLoadingPromiseWrapper>
  </Dialog>
</template>

<script setup>
/* eslint-disable css-modules/no-unused-class */
import Dialog from 'primevue/dialog';
import { defineProps, defineEmits, computed, ref, defineOptions, useAttrs } from 'vue';
import { useShadowDom } from '@src/mixins/useShadowDom';
import SbLoadingPromiseWrapper from '@src/components/Loading/SbLoadingPromiseWrapper.vue';
import SbCloseButton from '@src/components/Modal/SbCloseButton.vue';

defineOptions({
  name: 'SbModalNext',
});

const attrs = useAttrs();
const emit = defineEmits(['update:active', 'close']);
const maskId = 'mask-container';

const props = defineProps({
  /**
     * Boolean that determines whether or not to show the modal
     */
  active: {
    type: Boolean,
    required: true,
  },
  /**
     * A promise which will be used to apply loading styles until its resolved/rejected
    */
  loadingPromise: {
    type: Promise,
    default: null,
  },
  /**
     * A boolean that allows the onclose event to be fired via hitting the escape key.
    */
  escapeTrigger: {
    type: Boolean,
    default: true,
  },
  /**
     * A boolean that allows the onclose event to be fired via clicking outside the modal.
    */
  clickOutsideTrigger: {
    type: Boolean,
    default: () => true,
  },
  /**
     * Classes to be applied to the modal panel as the top most element is the underlay.
    */
  contentClasses: {
    type: Array,
    default: () => ([]),
  },
  /**
     * Classes to be applied to the modal panel as the top most element is the underlay.
    */
  headerClasses: {
    type: Array,
    default: () => ([]),
  },
  /**
     * A boolean that allows to show or hide close button.
     */
  showCloseButton: {
    type: Boolean,
    default: true,
  },
  /**
     * A boolean value that allows to show the modal in fullscreen mode.
     */
  showFullscreen: {
    type: Boolean,
    default: false,
  },
  /**
       * Node to attach the modal root component
      */
  appendTo: {
    type: [String, HTMLElement],
    default: null,
  },
  /**
     * A string that allows to show the modal in actionSheet or modal mode.
     * `modal, actionSheet`
     */
  variation: {
    type: String,
    default: 'modal',
  },
});

/** Register mask click listener to close the modal on clicking the mask elemeent */
/* Dialog.dismissableMask prop is not working */
const maskClickHandler = ref((event) => {
  if (event.target.id !== maskId) return;
  emit('close', event);
});

const isDraggable = computed(() => {
  return attrs.draggable === true;
});

function registerMaskHandler() {
  const maskContainer = document.getElementById(maskId);
  if (maskContainer) maskContainer.addEventListener('click', maskClickHandler.value);
}

function unregisterMaskHandler() {
  const maskContainer = document.getElementById(maskId);
  if (maskContainer) maskContainer.removeEventListener('click', maskClickHandler.value);
}

const appendToValue = props.appendTo || useShadowDom();
const shouldAppendTo = computed(() => !!props.appendTo);
const position = computed(() => {
  return props.variation === 'actionSheet' ? 'bottom' : 'center';
});
</script>
